import { Carousel } from '@mantine/carousel'
import {
  ActionIcon,
  Box,
  Group,
  Image,
  Modal,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core'
import { modals } from '@mantine/modals'
import type { AttachmentResponse } from '@markku/types'
import { IconEdit, IconTrash } from '@tabler/icons-react'
import { useEffect, useState } from 'react'

// Support both AttachmentResponse and simple photo types
type SimplePhoto = {
  id: number
  downloadUrl?: string | null
  mimeType?: string
}

type Photo = AttachmentResponse | SimplePhoto

// Type guard to check if photo is AttachmentResponse
function isAttachmentResponse(photo: Photo): photo is AttachmentResponse {
  return photo != null && 'filename' in photo
}

// Helper to check if media is a video
function isVideo(photo: Photo): boolean {
  if (isAttachmentResponse(photo)) {
    return photo.mimeType?.startsWith('video/') ?? false
  }
  return photo.mimeType?.startsWith('video/') ?? false
}

interface PhotoPreviewModalProps {
  photos: Photo[]
  initialIndex: number
  opened: boolean
  onClose: () => void
  onDelete?: (photoId: number) => void
  disabled?: boolean
  label?: string
  onUpdate?: (id: number, description: string) => Promise<void>
  showConfirmation?: boolean
}

export function PhotoPreviewModal({
  photos,
  initialIndex,
  opened,
  onClose,
  onDelete,
  disabled = false,
  label,
  onUpdate,
  showConfirmation = false,
}: PhotoPreviewModalProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex)

  // Sync currentIndex with initialIndex when modal opens
  useEffect(() => {
    if (opened) {
      setCurrentIndex(initialIndex)
    }
  }, [opened, initialIndex])

  const currentPhoto = photos[currentIndex]

  const openEditDescriptionModal = () => {
    if (!currentPhoto || !onUpdate || !isAttachmentResponse(currentPhoto))
      return

    let descriptionValue = currentPhoto.description || ''

    modals.openConfirmModal({
      title: 'Muokkaa kuvausta',
      centered: true,
      children: (
        <Stack gap="md">
          <TextInput
            label="Kuvaus"
            placeholder="Kuvan kuvaus"
            defaultValue={descriptionValue}
            onChange={(e) => (descriptionValue = e.currentTarget.value)}
            data-autofocus
          />
        </Stack>
      ),
      labels: { confirm: 'Tallenna', cancel: 'Peruuta' },
      onConfirm: async () => {
        await onUpdate(currentPhoto.id, descriptionValue)
      },
    })
  }

  const handleDelete = () => {
    if (!onDelete || !currentPhoto) return

    if (showConfirmation && isAttachmentResponse(currentPhoto)) {
      modals.openConfirmModal({
        title: 'Poista kuva',
        centered: true,
        children: (
          <Text>
            Haluatko varmasti poistaa kuvan
            {currentPhoto.description ? ` "${currentPhoto.description}"` : ''}?
          </Text>
        ),
        labels: { confirm: 'Poista', cancel: 'Peruuta' },
        confirmProps: { color: 'red' },
        onConfirm: async () => {
          await onDelete(currentPhoto.id)
          onClose()
        },
      })
    } else {
      onDelete(currentPhoto.id)
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      fullScreen
      padding={0}
      withCloseButton={false}
      styles={{
        body: {
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box bg="dark.9" p="md">
        <Group justify="space-between" wrap="nowrap">
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Text c="white" fw={500}>
              {label && `${label} `}({currentIndex + 1}/{photos.length})
              {isAttachmentResponse(currentPhoto) &&
                currentPhoto?.description && (
                  <>
                    {' - '}
                    {currentPhoto.description}
                  </>
                )}
            </Text>
          </Box>

          <Group gap={4}>
            {onUpdate && (
              <Tooltip label="Muokkaa kuvausta">
                <ActionIcon
                  onClick={openEditDescriptionModal}
                  size="lg"
                  variant="subtle"
                  c="white"
                >
                  <IconEdit size={18} />
                </ActionIcon>
              </Tooltip>
            )}
            {onDelete && !disabled && (
              <Tooltip label="Poista kuva">
                <ActionIcon
                  onClick={handleDelete}
                  size="lg"
                  variant="subtle"
                  c="white"
                >
                  <IconTrash size={18} />
                </ActionIcon>
              </Tooltip>
            )}
            <ActionIcon onClick={onClose} size="lg" variant="subtle" c="white">
              ✕
            </ActionIcon>
          </Group>
        </Group>
      </Box>

      <Box
        style={{
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          backgroundColor: 'var(--mantine-color-dark-9)',
        }}
      >
        <Carousel
          withIndicators={photos.length > 1}
          withControls={photos.length > 1}
          initialSlide={initialIndex}
          height="100%"
          onSlideChange={setCurrentIndex}
          styles={{
            root: { width: '100%', height: '100%' },
            viewport: { height: '100%' },
            container: { height: '100%' },
            slide: {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            },
          }}
        >
          {photos.map((photo) => (
            <Carousel.Slide key={photo.id}>
              {isVideo(photo) ? (
                <video
                  src={photo.downloadUrl ?? undefined}
                  controls
                  playsInline
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                  }}
                />
              ) : (
                <Image
                  src={photo.downloadUrl}
                  alt={
                    isAttachmentResponse(photo)
                      ? photo.description || photo.filename
                      : 'Photo'
                  }
                  fit="contain"
                  style={{ maxWidth: '100%', maxHeight: '100%' }}
                />
              )}
            </Carousel.Slide>
          ))}
        </Carousel>
      </Box>
    </Modal>
  )
}
